// src/components/bxpart/BxpartEditV1.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  Alert,
} from "react-bootstrap";
import { AsyncPaginate } from "react-select-async-paginate";
import Select, { components } from "react-select";
import { authFetch } from "../utils/authFetch";

const LIMIT = 20;

const TIP_OPTIONS = [
  { value: 1, label: "Servicii primite" },
  { value: 2, label: "Obiecte" },
  { value: 3, label: "Servicii prestate" },
];

const HighlightedOption = (props) => {
  const q = (props.selectProps.inputValue || "").trim();
  const label = props.data.label || "";
  const parts = q ? label.split(new RegExp(`(${q})`, "i")) : [label];
  return (
    <components.Option {...props}>
      {parts.map((p, i) =>
        q && p.toLowerCase() === q.toLowerCase() ? (
          <mark key={i}>{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </components.Option>
  );
};

function useLoader(resource, makeLabel) {
  return useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      const skip = (page - 1) * LIMIT;

      try {
        const data = await authFetch(
          `/${resource}?q=${encodeURIComponent(q)}&search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}`,
        );

        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.results)
              ? data.results
              : Array.isArray(data?.data)
                ? data.data
                : [];

        const total =
          typeof data?.total === "number"
            ? data.total
            : typeof data?.count === "number"
              ? data.count
              : typeof data?.total_count === "number"
                ? data.total_count
                : undefined;

        const options = items.map((it) => ({
          value: it.id,
          label:
            makeLabel?.(it) ??
            it.denumire ??
            it.name ??
            it.cod ??
            it.code ??
            it.descriere ??
            `#${it.id}`,
          __raw: it,
        }));

        const hasMore =
          typeof total === "number"
            ? skip + items.length < total
            : items.length === LIMIT;

        return { options, hasMore, additional: { page: page + 1 } };
      } catch (err) {
        console.error("loadOptions error", resource, err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [resource, makeLabel],
  );
}

export default function BxpartEditV1({ beforeSave } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState(null);

  // state pentru selecturile async
  const [optUM, setOptUM] = useState(null);
  const [optGrupa, setOptGrupa] = useState(null);

  const makeUmLabel = useCallback(
    (u) =>
      [u.cod || u.code || "", u.denumire || u.name || ""]
        .filter(Boolean)
        .join(" — ") || `UM #${u.id}`,
    [],
  );
  const makeGrupaLabel = useCallback(
    (g) =>
      [g.code || g.cod || "", g.denumire || g.name || ""]
        .filter(Boolean)
        .join(" — ") || `Grupă #${g.id}`,
    [],
  );

  const loadUmOptions = useLoader("bxums", makeUmLabel);
  const loadGrupaOptions = useLoader("grupa_produse", makeGrupaLabel);
  const additional = useMemo(() => ({ page: 1 }), []);

  // încarcă înregistrarea + preselect pentru dropdown-uri
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await authFetch(`/bxpart/${id}`);
        setForm({
          cod: data.cod ?? "",
          denumire: data.denumire ?? "",
          um_id: data.um_id ?? null,
          grupa_produse_id: data.grupa_produse_id ?? null,
          tip_grupa:
            data.tip_grupa_produse ?? data.bxpart_tip_grupa ?? data.grupa_produse?.tip_grupa ?? null,
          cpv: data.cpv ?? "",
          nc8: data.nc8 ?? "",
          ean: data.ean ?? "",
          kg_net: data.kg_net ?? 0,
          kg_brut: data.kg_brut ?? 0,
        });

        // preselect UM (din obiect dacă există)
        if (data?.um?.id) {
          const u = data.um;
          setOptUM({ value: u.id, label: makeUmLabel(u), __raw: u });
          setForm((f) => ({ ...f, um_id: u.id }));
        } else if (data.um_id) {
          try {
            const u = await authFetch(`/bxums/${data.um_id}`);
            setOptUM({ value: u.id, label: makeUmLabel(u), __raw: u });
          } catch {
            setOptUM({ value: data.um_id, label: `UM #${data.um_id}` });
          }
        } else {
          setOptUM(null);
        }

        // preselect Grupă produse
        const grupaId =
          data?.grupa_produse?.id ?? data?.grupa_produse_id ?? null;

        if (grupaId) {
          if (data?.grupa_produse?.id) {
            const g = data.grupa_produse;
            setOptGrupa({ value: g.id, label: makeGrupaLabel(g), __raw: g });
            setForm((f) => ({ ...f, grupa_produse_id: g.id }));
          } else {
            try {
              const g = await authFetch(`/grupa_produse/${grupaId}`);
              setOptGrupa({ value: g.id, label: makeGrupaLabel(g), __raw: g });
            } catch {
              setOptGrupa({ value: grupaId, label: `Grupă #${grupaId}` });
            }
          }
        } else {
          setOptGrupa(null);
        }
      } catch (e) {
        setErr(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, makeUmLabel, makeGrupaLabel]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? value === ""
              ? ""
              : Number(value)
            : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await beforeSave?.();
      const payload = {
        ...form,
        um_id: form.um_id ? Number(form.um_id) : null,
        grupa_produse_id: form.grupa_produse_id
          ? Number(form.grupa_produse_id)
          : null,
        tip_grupa_produse: form.tip_grupa ? Number(form.tip_grupa) : null,
        kg_net: Number(form.kg_net) || 0,
        kg_brut: Number(form.kg_brut) || 0,
      };
      await authFetch(`/bxpart/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      navigate("/bxpart");
    } catch (err) {
      setErr("Eroare la salvare: " + (err?.message ?? "unknown"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <Spinner animation="border" size="sm" /> Se încarcă…
      </Container>
    );
  }
  if (!form) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          {err || "Nu s-a putut încărca înregistrarea."}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card className="p-3">
        <h3>Editează Bxpart {form?.cod || `#${id}`}</h3>
        {err && (
          <Alert variant="danger" className="mt-2">
            {err}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>cod</Form.Label>
                <Form.Control
                  type="text"
                  name="cod"
                  value={form.cod ?? ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>denumire</Form.Label>
                <Form.Control
                  type="text"
                  name="denumire"
                  value={form.denumire ?? ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>UM</Form.Label>
                <AsyncPaginate
                  value={optUM}
                  loadOptions={loadUmOptions}
                  defaultOptions
                  additional={additional}
                  onChange={(opt) => {
                    setOptUM(opt);
                    setForm((f) => ({ ...f, um_id: opt?.value ?? null }));
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Grupă produse</Form.Label>
                <AsyncPaginate
                  value={optGrupa}
                  loadOptions={loadGrupaOptions}
                  defaultOptions
                  additional={additional}
                  onChange={(opt) => {
                    setOptGrupa(opt);
                    setForm((f) => ({
                      ...f,
                      grupa_produse_id: opt?.value ?? null,
                      tip_grupa: opt?.__raw?.tip_grupa ?? f.tip_grupa,
                    }));
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tip grupă produse</Form.Label>
                <Select
                  classNamePrefix="rs"
                  isClearable
                  options={TIP_OPTIONS}
                  value={
                    TIP_OPTIONS.find(
                      (o) => o.value === Number(form?.tip_grupa),
                    ) || null
                  }
                  onChange={(opt) =>
                    setForm((f) => ({ ...f, tip_grupa: opt?.value ?? null }))
                  }
                  placeholder="Alege tipul…"
                  components={{ Option: HighlightedOption }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>cpv</Form.Label>
                <Form.Control
                  type="text"
                  name="cpv"
                  value={form.cpv ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>nc8</Form.Label>
                <Form.Control
                  type="text"
                  name="nc8"
                  value={form.nc8 ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ean</Form.Label>
                <Form.Control
                  type="text"
                  name="ean"
                  value={form.ean ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>kg_net</Form.Label>
                <Form.Control
                  type="number"
                  name="kg_net"
                  value={form.kg_net ?? 0}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>kg_brut</Form.Label>
                <Form.Control
                  type="number"
                  name="kg_brut"
                  value={form.kg_brut ?? 0}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="text-end">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Se salvează…" : "Salvează"}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
